import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { Search, Filter, X } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DebouncedInput } from "@/components/ui/debounced-input"
import type { AuditHistoryFilters } from "@/types/ui/audit"
import type { AuditFieldConfig } from "@/lib/audit/audit-config"

interface HistoryFiltersProps {
  config: AuditFieldConfig
  filters: AuditHistoryFilters
  onFiltersChange: (filters: AuditHistoryFilters) => void
  totalResults: number
  uniqueStatuses: string[]
  uniqueUsers: string[]
  uniqueActions: string[]
}

export function HistoryFilters({
  config,
  filters,
  onFiltersChange,
  uniqueStatuses,
  uniqueUsers,
  uniqueActions,
  totalResults,
}: HistoryFiltersProps) {
  // Explicitly destructure only what's needed from config
  const { availableSections, formatStatus } = config

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, searchQuery: value })
  }

  const handleStatusToggle = (status: string) => {
    const currentStatuses = filters.statusFilter || []
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status]

    onFiltersChange({ ...filters, statusFilter: newStatuses })
  }

  const handleSectionToggle = (section: string) => {
    const currentSections = filters.sectionFilter || []
    const newSections = currentSections.includes(section)
      ? currentSections.filter((s) => s !== section)
      : [...currentSections, section]

    onFiltersChange({ ...filters, sectionFilter: newSections })
  }

  const handleClearFilters = () => {
    onFiltersChange({
      searchQuery: "",
      statusFilter: [],
      sectionFilter: [],
      userFilter: [],
      actionFilter: [],
      showSystemFields: false,
      hideDeletedVersions: true,
    })
  }

  const activeFilterCount =
    (filters.statusFilter?.length || 0) +
    (filters.sectionFilter?.length || 0) +
    (filters.userFilter?.length || 0) +
    (filters.actionFilter?.length || 0) +
    (filters.showSystemFields ? 1 : 0)

  const handleStatusChange = (value: string) => {
    if (value === "all") {
      onFiltersChange({ ...filters, statusFilter: [] })
    } else {
      onFiltersChange({ ...filters, statusFilter: [value] })
    }
  }

  const handleUserChange = (value: string) => {
    if (value === "all") {
      onFiltersChange({ ...filters, userFilter: [] })
    } else {
      onFiltersChange({ ...filters, userFilter: [value] })
    }
  }

  const handleActionChange = (value: string) => {
    if (value === "all") {
      onFiltersChange({ ...filters, actionFilter: [] })
    } else {
      onFiltersChange({ ...filters, actionFilter: [value] })
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters Row - Wrappable */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative w-full xl:w-auto xl:flex-1 xl:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <DebouncedInput
            placeholder="Search by field, user, or value..."
            value={filters.searchQuery || ""}
            onChange={handleSearchChange}
            debounceMs={300}
            className="pl-9 pr-9 w-full"
          />
          {filters.searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => onFiltersChange({ ...filters, searchQuery: "" })}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Status Filter */}
        <Select value={filters.statusFilter?.[0] || "all"} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[180px] bg-background">
            <SelectValue placeholder="Select Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {uniqueStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {formatStatus(status)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* User Filter */}
        <Select value={filters.userFilter?.[0] || "all"} onValueChange={handleUserChange}>
          <SelectTrigger className="w-[200px] bg-background">
            <SelectValue placeholder="Select User" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {uniqueUsers.map((user) => (
              <SelectItem key={user} value={user}>
                {user}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Action Filter */}
        <Select value={filters.actionFilter?.[0] || "all"} onValueChange={handleActionChange}>
          <SelectTrigger className="w-[180px] bg-background">
            <SelectValue placeholder="Select Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {uniqueActions.map((action) => (
              <SelectItem key={action} value={action}>
                {action}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Section Filter */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2 bg-transparent">
              <Filter className="h-4 w-4" />
              Section
              {(filters.sectionFilter?.length || 0) > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                  {filters.sectionFilter?.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="start">
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Filter by Section</h4>
              <div className="space-y-2">
                {availableSections.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 cursor-pointer hover:bg-accent rounded p-1 -mx-1 px-2"
                  >
                    <Checkbox
                      checked={filters.sectionFilter?.includes(option.value)}
                      onCheckedChange={() => handleSectionToggle(option.value)}
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* System Fields Toggle */}
        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <Checkbox
            checked={filters.showSystemFields}
            onCheckedChange={(checked) => onFiltersChange({ ...filters, showSystemFields: checked === true })}
          />
          <span className="text-sm text-muted-foreground">Show system fields</span>
        </label>

        {/* Clear Filters */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters} className="gap-2">
            <X className="h-4 w-4" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {(filters.statusFilter?.length || 0) > 0 || (filters.sectionFilter?.length || 0) > 0 ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Active filters:</span>

          {filters.statusFilter?.map((status) => (
            <Badge key={status} variant="secondary" className="gap-1.5">
              {formatStatus(status)}
              <button
                onClick={() => handleStatusToggle(status)}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}

          {filters.sectionFilter?.map((section) => (
            <Badge key={section} variant="secondary" className="gap-1.5">
              {section}
              <button
                onClick={() => handleSectionToggle(section)}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{totalResults}</span>{" "}
        {totalResults === 1 ? "change event" : "change events"}
      </div>
    </div>
  )
}
